import { yupResolver } from '@hookform/resolvers/yup'
import CloseIcon from '@mui/icons-material/Close'
import HelpIcon from '@mui/icons-material/Help'
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material'
import AccountNumberHelp1 from 'assets/images/account-number-help-1.png'
import AccountNumberHelp2 from 'assets/images/account-number-help-2.png'
import { CloudPay, ErrorText, MButton } from 'components'
import { MESSAGES } from 'config'
import { color } from 'config/color'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { APIs, type IVerifyPayBill } from 'services'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import * as yup from 'yup'
import { FormLabel } from './LoginForm'


const schema = yup
  .object({
    accountId: yup.string().required('Account number is required'),
    lastName: yup.string().required('Last name is required')
  })
  .required()


interface PayFormProps {
  handleTabChange: (_e: any, tabNumber: number) => void
}

export const PayForm: React.FC<PayFormProps> = ({ handleTabChange }: PayFormProps) => {
  const [account, setAccount] = useState<string>('')
  const [cloudPayVisible, setCloudPayVisible] = useState<boolean>(false)
  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<IVerifyPayBill>({
    mode: 'all',
    defaultValues: {
      accountId: '',
      lastName: ''
    },
    resolver: yupResolver(schema)
  })
  const { enqueueSnackbar } = useSnackbar()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const switchToAnotherTab = (): void => {
    handleTabChange(null, 1)
  }
  const isMobile = useMobileBreakpoints()

  const onSubmit = async (body: IVerifyPayBill): Promise<void> => {
    try {
      const response = await APIs.verifyPayBill(body)
      if (response.success === true) {
        setAccount(body.accountId)
        setCloudPayVisible(true)
      } else {
        enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR_TEMP, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR_TEMP, { variant: 'error' })
    }
  }


  const handleCloudPayClose = (): void => {
    setCloudPayVisible(false)
  }

  const handleModalOpen = (): void => {
    setIsModalOpen(!isModalOpen)
  }


  return (
    <>
      <form method="post" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Typography variant="h2" fontSize={24} color={color.grey[900]}>Enter your account number to proceed with your payment</Typography>
            <Typography fontSize={14} color={color.grey[700]}>If you want to log in or register into the portal, <Link href="#login-or-register" aria-label="Click here to log in or register" sx={{ cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${color.primary[500]}`, outlineOffset: 2, borderRadius: 0.5 } }} onClick={(e) => { e.preventDefault(); switchToAnotherTab() }}>click here</Link>.</Typography>
          </Box>
          <Controller
            name="accountId"
            control={control}
            render={({ field: { onChange, value, name } }): JSX.Element => (
              <Box>
                <Box display="flex" alignItems="center">
                  <FormLabel htmlFor="accountId">Account Number <IconButton onClick={handleModalOpen} sx={{ padding: 0, minWidth: 24, minHeight: 24 }} aria-label="account number help"><HelpIcon color="primary" sx={{ fontSize: 24 }} /></IconButton></FormLabel>
                </Box>
                <OutlinedInput
                  id="accountId"
                  name={name}
                  placeholder="Enter your account number"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
                {(errors.accountId != null) && <ErrorText>{errors.accountId.message}</ErrorText>}
              </Box>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, value, name } }): JSX.Element => (
              <Box>
                <FormLabel htmlFor="lastName">Last Name / Business Name</FormLabel>
                <OutlinedInput
                  id="lastName"
                  name={name}
                  placeholder="Enter your last name"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
                {(errors.lastName != null) && <ErrorText>{errors.lastName.message}</ErrorText>}
              </Box>
            )}
          />

        </Box>
        <Box my={3} display="flex" flexDirection="column" gap={2}>
          <MButton
            variant="contained"
            type="submit"
            size="large"
            rounded="true"
            disabled={!isValid}
            texttransform="none"
            fullWidth
            onSubmit={handleSubmit(onSubmit)}
            sx={{ fontSize: 16 }}
          >
            Pay your Bill
          </MButton>
          <MButton
            variant="outlined"
            type="submit"
            size="large"
            rounded="true"
            texttransform="none"
            fullWidth
            onSubmit={handleSubmit(onSubmit)}
            onClick={switchToAnotherTab}
            sx={{ fontSize: 16 }}
          >
            Login or Register
          </MButton>
        </Box>
      </form >
      {cloudPayVisible && (
        <CloudPay account={account} onClose={handleCloudPayClose} />
      )}
      {isModalOpen && <Dialog open={isModalOpen} onClose={handleModalOpen} aria-labelledby="account-number-help-dialog-title" maxWidth="lg" fullScreen={!!isMobile}>
        <Box m={3} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={handleModalOpen} aria-label="close help dialog"><CloseIcon color="primary" /></IconButton>
          </Box>
          <Typography id="account-number-help-dialog-title" component="h2" color={color.grey[900]} fontWeight={500} fontSize={24} textAlign="center">Where can I find my account number?</Typography>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
            <Box display="flex" flexDirection="column" gap={2} p={2} alignItems="center" justifyContent="center" border={`solid 1px ${color.background[300]}`} borderRadius={2} bgcolor={color.background[50]}>
              <Avatar sx={{ backgroundColor: color.background[50], padding: 1 }}><Typography color={color.primary[800]} fontSize={18}>1</Typography></Avatar>
              <Typography color={color.grey[700]} fontWeight={400} fontSize={16} textAlign="center">Get one of your previous bills</Typography>
              <Box component="img" alt="Example bill indicating where the account summary section appears on a previous statement." src={AccountNumberHelp1} width="100%"></Box>
            </Box>
            <Box display="flex" flexDirection="column" gap={2} p={2} alignItems="center" justifyContent="center" border={`solid 1px ${color.background[300]}`} borderRadius={2} bgcolor={color.background[50]}>
              <Avatar sx={{ backgroundColor: color.background[50], padding: 1 }}><Typography color={color.primary[800]} fontSize={18}>2</Typography></Avatar>
              <Typography color={color.grey[700]} fontWeight={400} fontSize={16} textAlign="center">Check the right hand corner, under “Account Summary”</Typography>
              <Box component="img" alt="Example bill highlighting the account number in the account summary section on the right side." src={AccountNumberHelp2} width="100%"></Box>
            </Box>
          </Box>
        </Box>


      </Dialog>}

    </>
  )
}
